import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Chip,
  Paper,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Switch,
  Tooltip,
} from '@mui/material';
import {
  PlayArrow,
  Pause,
  Delete,
  Download,
  RecordVoiceOver,
  RssFeed as RssIcon,
  Add,
  Edit,
  AutoAwesome,
  GraphicEq,
  Campaign,
  Block,
  EditNote,
  MusicNote,
  Refresh,
  Tune,
  CloudQueue,
  PlaylistAddCheck,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import PageHeader from '../components/PageHeader';
import PromptGenerator from '../components/PromptGenerator';
import PromoManager from '../components/PromoManager';
import BannedWordsManager from '../components/BannedWordsManager';
import GeneratedLinksReview from '../components/GeneratedLinksReview';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

const ACCENT = '#22d3ee';

const ELEVENLABS_MODELS = [
  { id: 'eleven_v4', label: 'Eleven v4 (90+ languages, most expressive — default)' },
  { id: 'eleven_v4_turbo', label: 'Eleven v4 Turbo (90+ languages, ~100ms — audio tags)' },
  { id: 'eleven_v3', label: 'Eleven v3 (70+ languages, expressive)' },
  { id: 'eleven_v3_conversational', label: 'Eleven v3 Conversational (~165ms, live)' },
  { id: 'eleven_multilingual_v2', label: 'Eleven Multilingual v2 (29 languages, long-form)' },
  { id: 'eleven_flash_v2_5', label: 'Eleven Flash v2.5 (32 languages, fastest)' },
];

const SPEECHIFY_MODELS = [
  { id: 'simba-3.2', label: 'Simba 3.2 (recommended English)' },
  { id: 'simba-3.0', label: 'Simba 3.0 (English + 6 European languages)' },
  { id: 'simba-multilingual', label: 'Simba Multilingual (legacy, 30+ languages)' },
  { id: 'simba-english', label: 'Simba English (legacy)' },
];

const DEFAULT_GOOGLE_LANGUAGE = 'en-US';

// Shared surface styles for the redesigned AI Content page
const sectionCardSx = {
  bgcolor: 'var(--rs-paper)',
  border: '1px solid rgba(148,163,184,0.14)',
  borderRadius: 3,
  overflow: 'hidden',
  animation: 'card-rise 420ms ease-out both',
};

const insetPaperSx = {
  p: 2,
  bgcolor: 'var(--rs-overlay-soft)',
  border: '1px solid rgba(148,163,184,0.14)',
  borderRadius: 2.5,
};

const ctaButtonSx = {
  px: 3,
  py: 1.1,
  borderRadius: 2.5,
  fontWeight: 700,
  color: '#06222a',
  backgroundImage: 'linear-gradient(120deg, #67e8f9 0%, #22d3ee 45%, #0891b2 100%)',
  boxShadow: '0 10px 28px -10px rgba(34,211,238,0.55)',
  transition: 'transform 160ms ease, box-shadow 160ms ease',
  '&:hover': {
    backgroundImage: 'linear-gradient(120deg, #7ceefc 0%, #34dbf4 45%, #0aa2c8 100%)',
    boxShadow: '0 14px 34px -10px rgba(34,211,238,0.7)',
    transform: 'translateY(-1px)',
  },
  '&.Mui-disabled': {
    backgroundImage: 'none',
    bgcolor: 'rgba(148,163,184,0.12)',
    color: 'text.disabled',
    boxShadow: 'none',
  },
};

const outlineButtonSx = {
  borderRadius: 2.5,
  color: ACCENT,
  borderColor: 'rgba(34,211,238,0.4)',
  '&:hover': { borderColor: ACCENT, bgcolor: 'rgba(34,211,238,0.08)' },
};

function SectionHeader({ icon, title, subtitle, action }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3, flexWrap: 'wrap' }}>
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          color: ACCENT,
          background: 'linear-gradient(145deg, rgba(34,211,238,0.18), rgba(59,130,246,0.10))',
          border: '1px solid rgba(34,211,238,0.28)',
          boxShadow: '0 8px 20px -10px rgba(34,211,238,0.45)',
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 220 }}>
        <Typography variant="h6" sx={{ color: 'var(--rs-text)', lineHeight: 1.25 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ color: 'var(--rs-muted)', mt: 0.5 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {action}
    </Box>
  );
}

function MusicBedControls({ enabled, onToggle, categoryId, onCategoryChange, volumeDb, onVolumeChange, categories, description }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2.5,
        border: enabled ? '1px solid rgba(34,211,238,0.35)' : '1px solid rgba(148,163,184,0.14)',
        bgcolor: enabled ? 'rgba(34,211,238,0.05)' : 'var(--rs-surface)',
        transition: 'border-color 160ms ease, background-color 160ms ease',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <MusicNote sx={{ color: enabled ? ACCENT : '#5b6675', fontSize: 22 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ color: 'var(--rs-text)' }}>
            Music Bed
          </Typography>
          <Typography variant="caption" sx={{ color: 'var(--rs-muted)' }}>
            {description}
          </Typography>
        </Box>
        <Switch
          checked={enabled}
          onChange={(e) => onToggle(e.target.checked)}
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: ACCENT },
          }}
        />
      </Box>

      {enabled && (
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: 'var(--rs-muted)' }}>Music Bed Category</InputLabel>
              <Select
                value={categoryId || ''}
                onChange={(e) => onCategoryChange(e.target.value || null)}
                label="Music Bed Category"
                sx={{ color: 'var(--rs-text)' }}
              >
                <MenuItem value="">Select Category</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.parent_category_id ? '↳ ' : ''}{cat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Music Bed Volume (dB)"
              value={volumeDb}
              onChange={(e) => onVolumeChange(parseFloat(e.target.value))}
              inputProps={{ min: -40, max: 0, step: 1 }}
              helperText="Volume for music bed (e.g. -12 dB)"
            />
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

export default function AIVoice() {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [myVoicesSubTab, setMyVoicesSubTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // TTS Generation State
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [ttsText, setTtsText] = useState('');
  const [generatedAudio, setGeneratedAudio] = useState(null);
  const [generationProgress, setGenerationProgress] = useState(null);
  const [isPlayingGenerated, setIsPlayingGenerated] = useState(false);
  const generatedAudioRef = useRef(null);

  // Cloned Voices List
  const [clonedVoices, setClonedVoices] = useState([]);
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [voiceLimit, setVoiceLimit] = useState(0);
  const [maxVoices, setMaxVoices] = useState(50);

  // Prompts for PromoManager and Content Generation
  const [prompts, setPrompts] = useState([]);
  
  // Generate Content State (using prompts)
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [selectedVoiceForContent, setSelectedVoiceForContent] = useState(null);
  const [generateDurationSeconds, setGenerateDurationSeconds] = useState(15);
  const [generatedContentScript, setGeneratedContentScript] = useState('');
  const [generatedContentWarnings, setGeneratedContentWarnings] = useState([]);
  const [generatedContentLength, setGeneratedContentLength] = useState(null);
  const [generatedContentAudio, setGeneratedContentAudio] = useState(null);
  const [contentGenerationProgress, setContentGenerationProgress] = useState(null);

  // ElevenLabs State
  const [elevenlabsVoices, setElevenlabsVoices] = useState([]);
  const [loadingElevenlabsVoices, setLoadingElevenlabsVoices] = useState(false);
  const [elevenlabsName, setElevenlabsName] = useState('');
  const [elevenlabsVoiceId, setElevenlabsVoiceId] = useState('');
  const [elevenlabsModelId, setElevenlabsModelId] = useState('eleven_v4');
  const [elevenlabsLanguage, setElevenlabsLanguage] = useState('en');
  const [elevenlabsDescription, setElevenlabsDescription] = useState('');

  // Speechify State
  const [speechifyVoices, setSpeechifyVoices] = useState([]);
  const [loadingSpeechifyVoices, setLoadingSpeechifyVoices] = useState(false);
  const [speechifyName, setSpeechifyName] = useState('');
  const [speechifyVoiceId, setSpeechifyVoiceId] = useState('');
  const [speechifyModel, setSpeechifyModel] = useState('simba-3.2');
  const [speechifyLanguage, setSpeechifyLanguage] = useState('en');
  const [speechifyDescription, setSpeechifyDescription] = useState('');
  const [speechifySearch, setSpeechifySearch] = useState('');
  const [speechifyGenderFilter, setSpeechifyGenderFilter] = useState('all');
  const [speechifyLocaleFilter, setSpeechifyLocaleFilter] = useState('all');

  // Google TTS State
  const [googleVoices, setGoogleVoices] = useState([]);
  const [loadingGoogleVoices, setLoadingGoogleVoices] = useState(false);
  const [googleName, setGoogleName] = useState('');
  const [googleVoiceId, setGoogleVoiceId] = useState('');
  const [googleLanguage, setGoogleLanguage] = useState(DEFAULT_GOOGLE_LANGUAGE);
  const [googleDescription, setGoogleDescription] = useState('');
  const [googleSearch, setGoogleSearch] = useState('');
  const [googleGenderFilter, setGoogleGenderFilter] = useState('all');

  // AI Config (which API keys are enabled)
  const [aiConfig, setAiConfig] = useState(null);

  // Voice Settings Dialog
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [editingVoice, setEditingVoice] = useState(null);
  const [bannedPhrases, setBannedPhrases] = useState([]);
  const [pronunciationRules, setPronunciationRules] = useState({});
  const [voiceSettings, setVoiceSettings] = useState({});
  const [editingModelId, setEditingModelId] = useState('');

  // RSS / website sources state
  const [rssFeeds, setRssFeeds] = useState([]);
  const [loadingRssFeeds, setLoadingRssFeeds] = useState(false);
  const [rssFeedDialogOpen, setRssFeedDialogOpen] = useState(false);
  const [editingRssFeed, setEditingRssFeed] = useState(null);
  const [refreshingSourceId, setRefreshingSourceId] = useState(null);
  const [rssPreview, setRssPreview] = useState(null);
  const [previewingSource, setPreviewingSource] = useState(false);
  const defaultRssForm = () => ({
    name: '',
    url: '',
    sourceType: 'website',
    maxItems: 5,
    maxLinks: 5,
    maxRunsPerDay: 5,
    refreshIntervalMinutes: '',
    isActive: true,
  });
  const [rssForm, setRssForm] = useState(defaultRssForm);
  const [removeSilence, setRemoveSilence] = useState(true);

  // Music Bed State (for Generate Content and Generate Speech)
  const [musicBedEnabled, setMusicBedEnabled] = useState(false);
  const [musicBedCategories, setMusicBedCategories] = useState([]);
  const [musicBedCategoryId, setMusicBedCategoryId] = useState(null);
  const [musicBedVolumeDb, setMusicBedVolumeDb] = useState(-12);
  // Separate music bed state for Generate Speech tab
  const [speechMusicBedEnabled, setSpeechMusicBedEnabled] = useState(false);
  const [speechMusicBedCategoryId, setSpeechMusicBedCategoryId] = useState(null);
  const [speechMusicBedVolumeDb, setSpeechMusicBedVolumeDb] = useState(-12);

  useEffect(() => {
    fetchClonedVoices();
    fetchRssFeeds();
    fetchMusicBedCategories();
    fetchAIConfig();
  }, []);

  useEffect(() => {
    if (tabValue === 0 || tabValue === 3) {
      fetchPrompts();
    }
  }, [tabValue]);

  useEffect(() => {
    fetchMusicBedCategories();
  }, [user?.stationId]);

  // Fetch prompts for content generation
  const fetchPrompts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ai/voices/prompts`, {
        withCredentials: true,
      });
      setPrompts(res.data || []);
    } catch (err) {
      console.error('Error fetching prompts:', err);
    }
  };

  // Fetch RSS feeds
  const fetchRssFeeds = async () => {
    setLoadingRssFeeds(true);
    try {
      const res = await axios.get(`${API_URL}/api/ai/rss-feeds`, {
        withCredentials: true,
      });
      setRssFeeds(res.data || []);
    } catch (err) {
      console.error('Error fetching RSS feeds:', err);
      setRssFeeds([]);
    } finally {
      setLoadingRssFeeds(false);
    }
  };

  // Handle RSS / website source form submit
  const handleRssFeedSubmit = async () => {
    if (!rssForm.name.trim() || !rssForm.url.trim()) {
      setError('Name and URL are required');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (editingRssFeed) {
        await axios.put(
          `${API_URL}/api/ai/rss-feeds/${editingRssFeed.id}`,
          rssForm,
          { withCredentials: true }
        );
        setSuccess('Source updated successfully');
      } else {
        const res = await axios.post(
          `${API_URL}/api/ai/rss-feeds`,
          rssForm,
          { withCredentials: true }
        );
        const preview = res.data?.preview;
        if (preview && preview.ok === false) {
          setSuccess('');
          setError(`Source saved, but it could not be read: ${preview.error}`);
        } else {
          setSuccess(
            preview?.note
              ? `Source added. ${preview.note}`
              : `Source added with ${preview?.items?.length || 0} item(s) ready to use`
          );
        }
      }
      setRssFeedDialogOpen(false);
      setEditingRssFeed(null);
      setRssPreview(null);
      setRssForm(defaultRssForm());
      fetchRssFeeds();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save source');
    } finally {
      setLoading(false);
    }
  };

  // Check a URL before saving it, so a wrong address is caught on the form
  const handlePreviewRssSource = async () => {
    if (!rssForm.url.trim()) {
      setError('Enter a URL to check');
      return;
    }
    setPreviewingSource(true);
    setError('');
    setRssPreview(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/ai/rss-feeds/preview`,
        { url: rssForm.url, sourceType: rssForm.sourceType, maxItems: rssForm.maxItems, maxLinks: rssForm.maxLinks },
        { withCredentials: true }
      );
      setRssPreview(res.data);
    } catch (err) {
      setRssPreview({ ok: false, error: err.response?.data?.message || 'Could not read that source' });
    } finally {
      setPreviewingSource(false);
    }
  };

  // Read a source now, outside its cycle
  const handleRefreshRssSource = async (feedId) => {
    setRefreshingSourceId(feedId);
    setError('');
    try {
      const res = await axios.post(`${API_URL}/api/ai/rss-feeds/${feedId}/refresh`, {}, { withCredentials: true });
      setSuccess(`Read ${res.data?.items?.length || 0} item(s) from ${res.data?.feed?.name || 'source'}`);
      fetchRssFeeds();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to read source');
      fetchRssFeeds();
    } finally {
      setRefreshingSourceId(null);
    }
  };

  // Handle RSS feed delete
  const handleDeleteRssFeed = async (feedId) => {
    if (!window.confirm('Are you sure you want to delete this RSS feed?')) {
      return;
    }

    setLoading(true);
    setError('');
    try {
      await axios.delete(`${API_URL}/api/ai/rss-feeds/${feedId}`, {
        withCredentials: true,
      });
      setSuccess('RSS feed deleted successfully');
      fetchRssFeeds();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete source');
    } finally {
      setLoading(false);
    }
  };

  // Handle edit RSS feed
  const handleEditRssFeed = (feed) => {
    setEditingRssFeed(feed);
    setRssPreview(null);
    setRssForm({
      name: feed.name || '',
      url: feed.url || '',
      sourceType: feed.source_type === 'website' ? 'website' : 'rss',
      maxItems: feed.max_items || 5,
      maxLinks: feed.max_links || 5,
      maxRunsPerDay: feed.max_runs_per_day || 5,
      refreshIntervalMinutes: feed.refresh_interval_minutes || '',
      isActive: feed.is_active !== false,
    });
    setRssFeedDialogOpen(true);
  };

  // Fetch music bed categories for music bed selection
  const fetchMusicBedCategories = async () => {
    try {
      const stationId = user?.stationId;
      const stationParams = (stationId || stationId === 0) ? { station_id: stationId } : {};
      const res = await axios.get(`${API_URL}/api/categories`, {
        params: stationParams,
        withCredentials: true
      });
      const allCats = res.data || [];
      const mbCats = allCats.filter(c => c.category_type === 'music_bed' || c.name === 'Music Beds');
      setMusicBedCategories(mbCats);

      // Also fetch subcategories of Music Beds
      const parentMB = allCats.find(c => c.name === 'Music Beds' || c.category_type === 'music_bed');
      if (parentMB) {
        try {
          const subRes = await axios.get(`${API_URL}/api/categories/${parentMB.id}/subcategories`, {
            params: stationParams,
            withCredentials: true
          });
          const subs = subRes.data || [];
          setMusicBedCategories(prev => [...prev, ...subs.filter(s => !prev.find(p => p.id === s.id))]);
        } catch (e) {
          // subcategories endpoint may not exist
        }
      }
    } catch (err) {
      console.error('Error fetching music bed categories:', err);
    }
  };

  const fetchAIConfig = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ai/config`, {
        withCredentials: true,
      });
      setAiConfig(res.data || null);
    } catch (err) {
      console.error('Error fetching AI config:', err);
    }
  };

  const hasElevenlabsApi = Boolean(
    aiConfig?.has_elevenlabs_api_key ?? aiConfig?.elevenlabs_api_key
  );
  const hasSpeechifyApi = Boolean(
    aiConfig?.has_speechify_api_key ?? aiConfig?.speechify_api_key
  );
  const hasGoogleApi = Boolean(
    aiConfig?.has_google_api_key ?? aiConfig?.google_api_key ?? aiConfig?.murf_api_key
  );

  const voiceSubTabs = [
    { key: 'my_voices', label: 'My Voices' },
    ...(hasElevenlabsApi ? [{ key: 'elevenlabs', label: 'Add ElevenLabs Voice' }] : []),
    ...(hasSpeechifyApi ? [{ key: 'speechify', label: 'Add Speechify Voice' }] : []),
    ...(hasGoogleApi ? [{ key: 'google', label: 'Add Google Voice' }] : []),
  ];

  const activeVoiceSubTabKey = voiceSubTabs[myVoicesSubTab]?.key || 'my_voices';

  useEffect(() => {
    if (myVoicesSubTab >= voiceSubTabs.length) {
      setMyVoicesSubTab(0);
    }
  }, [voiceSubTabs.length, myVoicesSubTab]);

  // Generate content using a prompt
  const handleGenerateContent = async () => {
    if (!selectedPrompt) {
      setError('Please select a prompt');
      return;
    }
    if (!selectedVoiceForContent) {
      setError('Please select a voice');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setContentGenerationProgress('Generating script...');
    setGeneratedContentScript('');
    setGeneratedContentWarnings([]);
    setGeneratedContentLength(null);
    setGeneratedContentAudio(null);

    try {
      // Step 1: Generate script using Grok with the prompt.
      // No song context is available on this screen, so any {artist}/{title} style tags in the
      // prompt are dropped and reported as warnings. Time tags are filled in server-side in
      // station time.
      const scriptRes = await axios.post(
        `${API_URL}/api/ai/generate-script-from-prompt`,
        {
          prompt_id: selectedPrompt.id,
          voice_id: selectedVoiceForContent.id,
          max_length: parseInt(generateDurationSeconds, 10) || selectedPrompt.max_length || 15,
          context: {},
        },
        { withCredentials: true }
      );

      const script = scriptRes.data?.script;
      if (!script) {
        throw new Error('Failed to generate script');
      }

      // The server reports dropped tags generically; on this screen the reason is simply that
      // there is no playlist to read from, so say that instead.
      const unresolved = Array.isArray(scriptRes.data?.unresolved_tags) ? scriptRes.data.unresolved_tags : [];
      const warnings = (Array.isArray(scriptRes.data?.warnings) ? scriptRes.data.warnings : [])
        .filter((w) => !(unresolved.length > 0 && /^Tags with no value/.test(String(w))));
      if (unresolved.length > 0) {
        warnings.push(
          `No song context here, so ${unresolved.map((t) => `{${t}}`).join(', ')} were left out. On air these are filled from the playlist.`
        );
      }
      setGeneratedContentScript(script);
      setGeneratedContentWarnings(warnings);
      setGeneratedContentLength({
        seconds: Number(scriptRes.data?.estimated_seconds) || null,
        overrun: scriptRes.data?.overrun === true,
        slot: parseInt(generateDurationSeconds, 10) || selectedPrompt.max_length || 15,
      });
      setContentGenerationProgress('Generating audio...');

      // Step 2: Generate audio from the script
      const audioRes = await axios.post(
        `${API_URL}/api/ai/voices/generate`,
        {
          voice_id: selectedVoiceForContent.id,
          text: script,
          ...(musicBedEnabled && musicBedCategoryId ? {
            use_music_bed: true,
            music_bed_category_id: musicBedCategoryId,
            music_bed_dip_level: musicBedVolumeDb,
          } : {}),
        },
        {
          withCredentials: true,
          responseType: 'blob',
          onDownloadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setContentGenerationProgress(`Generating audio: ${percentCompleted}%`);
            }
          },
        }
      );

      try {
        const ttsWarnings = JSON.parse(decodeURIComponent(audioRes.headers?.['x-script-warnings'] || '[]'));
        if (Array.isArray(ttsWarnings) && ttsWarnings.length > 0) {
          setGeneratedContentWarnings((prev) => [...prev, ...ttsWarnings]);
        }
      } catch (_) {
        // header absent or malformed — nothing to show
      }

      const audioUrl = URL.createObjectURL(audioRes.data);
      setGeneratedContentAudio(audioUrl);
      setContentGenerationProgress(null);
      setSuccess('Content generated successfully!');
    } catch (err) {
      console.error('Error generating content:', err);
      let message = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to generate content';
      // Blob error bodies (from the audio step) need decoding before we can show the message
      if (err.response?.data instanceof Blob) {
        try {
          const parsed = JSON.parse(await err.response.data.text());
          message = parsed.message || parsed.error || message;
        } catch (_) {
          // keep the generic message
        }
      }
      setError(message);
      setContentGenerationProgress(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchClonedVoices = async () => {
    setLoadingVoices(true);
    try {
      const res = await axios.get(`${API_URL}/api/ai/voices`, {
        withCredentials: true,
      });
      setClonedVoices(res.data?.voices || res.data || []);
      setVoiceLimit(res.data?.total || 0);
      setMaxVoices(res.data?.max_allowed || 50);
    } catch (err) {
      console.error('Error fetching voices:', err);
      setError('Failed to load voices');
    } finally {
      setLoadingVoices(false);
    }
  };

  const handleGenerateTTS = async () => {
    if (!selectedVoice) {
      setError('Please select a voice');
      return;
    }
    if (!ttsText.trim()) {
      setError('Please enter text to generate');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setGenerationProgress('Generating speech...');
    // Stop and clear any previously generated audio
    if (generatedAudioRef.current) {
      generatedAudioRef.current.pause();
      generatedAudioRef.current.src = '';
    }
    setGeneratedAudio(null);
    setIsPlayingGenerated(false);

    try {
      const useBackgroundGeneration = !isElevenLabsVoice(selectedVoice) && !isSpeechifyVoice(selectedVoice);

      if (useBackgroundGeneration) {
        const startRes = await axios.post(
          `${API_URL}/api/ai/voices/generate`,
          {
            voice_id: selectedVoice.id,
            text: ttsText.trim(),
            background: true,
            ...(speechMusicBedEnabled && speechMusicBedCategoryId ? {
              use_music_bed: true,
              music_bed_category_id: speechMusicBedCategoryId,
              music_bed_dip_level: speechMusicBedVolumeDb,
            } : {}),
          },
          {
            withCredentials: true,
          }
        );

        const jobId = startRes?.data?.job_id;
        if (!jobId) {
          throw new Error('Background generation did not return a job ID.');
        }

        setGenerationProgress('Generating in background...');

        const maxPollAttempts = 120;
        const pollDelayMs = 1500;
        let statusData = null;

        for (let attempt = 0; attempt < maxPollAttempts; attempt++) {
          await new Promise((resolve) => setTimeout(resolve, pollDelayMs));

          const statusRes = await axios.get(
            `${API_URL}/api/ai/voices/generate/jobs/${jobId}`,
            { withCredentials: true }
          );
          statusData = statusRes.data;

          if (statusData?.status === 'completed') {
            break;
          }

          if (statusData?.status === 'failed') {
            throw new Error(statusData?.message || 'Background generation failed.');
          }

          setGenerationProgress(`Generating in background... (${attempt + 1})`);
        }

        if (!statusData || statusData.status !== 'completed') {
          throw new Error('Background generation is taking too long. Please try again.');
        }

        setGenerationProgress('Downloading generated audio...');

        const audioRes = await axios.get(
          `${API_URL}/api/ai/voices/generate/jobs/${jobId}/audio`,
          {
            withCredentials: true,
            responseType: 'blob',
          }
        );

        const audioUrl = URL.createObjectURL(audioRes.data);
        setGeneratedAudio(audioUrl);
        setSuccess('Speech generated successfully in background! Saved to asset library.');
      } else {
        const res = await axios.post(
          `${API_URL}/api/ai/voices/generate`,
          {
            voice_id: selectedVoice.id,
            text: ttsText.trim(),
            ...(speechMusicBedEnabled && speechMusicBedCategoryId ? {
              use_music_bed: true,
              music_bed_category_id: speechMusicBedCategoryId,
              music_bed_dip_level: speechMusicBedVolumeDb,
            } : {}),
          },
          {
            withCredentials: true,
            responseType: 'blob',
            onDownloadProgress: (progressEvent) => {
              if (progressEvent.total) {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                setGenerationProgress(`Generating: ${percentCompleted}%`);
              }
            },
          }
        );

        const audioUrl = URL.createObjectURL(res.data);
        setGeneratedAudio(audioUrl);
        setSuccess('Speech generated successfully! Saved to asset library.');
      }

      setGenerationProgress(null);
      // Auto-play after a short delay so the element can mount
      setTimeout(() => {
        if (generatedAudioRef.current) {
          generatedAudioRef.current.play().catch(() => {});
        }
      }, 200);
    } catch (err) {
      console.error('Error generating TTS:', err);
      let errorMessage = err.response?.data?.message || 'Failed to generate speech.';

      if (err.response?.status === 504) {
        errorMessage = 'Speech generation timed out at the gateway. Try a shorter script or use a faster voice provider.';
      }

      if (err.response?.data instanceof Blob) {
        try {
          const blobText = await err.response.data.text();
          let parsed = null;
          try { parsed = blobText ? JSON.parse(blobText) : null; } catch (_) {}
          if (parsed?.message) {
            errorMessage = parsed.message;
          } else if (/504 Gateway Time-out|504 Gateway Timeout|openresty/i.test(blobText)) {
            errorMessage = 'Speech generation timed out at the gateway. Try a shorter script or use a faster voice provider.';
          } else if (blobText && blobText.length < 500) {
            errorMessage = `Server error: ${blobText}`;
          } else {
            errorMessage = `Failed to generate speech (HTTP ${err.response?.status}). Check server logs.`;
          }
        } catch {
          errorMessage = `Failed to generate speech (HTTP ${err.response?.status}). Check server logs.`;
        }
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
      setGenerationProgress(null);
    }
  };

  const handleDeleteVoice = async (voiceId) => {
    if (!window.confirm('Are you sure you want to delete this voice clone?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/ai/voices/${voiceId}`, {
        withCredentials: true,
      });
      setSuccess('Voice deleted successfully');
      fetchClonedVoices();
      if (selectedVoice?.id === voiceId) {
        setSelectedVoice(null);
        if (generatedAudioRef.current) { generatedAudioRef.current.pause(); }
        setGeneratedAudio(null);
        setIsPlayingGenerated(false);
      }
    } catch (err) {
      console.error('Error deleting voice:', err);
      setError('Failed to delete voice');
    }
  };

  const isElevenLabsVoice = (voice) => {
    if (!voice) return false;
    if (voice.voice_type === 'elevenlabs') return true;
    return Boolean(voice.elevenlabs_voice_id && !voice.speechify_voice_id && !voice.reference_audio_path);
  };

  const isSpeechifyVoice = (voice) => {
    if (!voice) return false;
    if (voice.voice_type === 'speechify') return true;
    return Boolean(voice.speechify_voice_id && !voice.reference_audio_path);
  };

  const isGoogleVoice = (voice) => {
    if (!voice) return false;
    if (voice.voice_type === 'google') return true;
    return (voice.voice_settings?.provider || voice.settings?.provider) === 'google';
  };

  const getVoiceProviderLabel = (voice) => {
    if (isGoogleVoice(voice)) return 'Google';
    if (isSpeechifyVoice(voice)) return 'Speechify';
    if (isElevenLabsVoice(voice)) return 'ElevenLabs';
    return 'Cloned';
  };

  const getVoiceProviderColor = (voice) => {
    if (isGoogleVoice(voice)) return '#34a853';
    if (isSpeechifyVoice(voice)) return '#f59e0b';
    if (isElevenLabsVoice(voice)) return '#a855f7';
    return '#22c55e';
  };

  const handleDownloadAudio = () => {
    if (generatedAudio) {
      const a = document.createElement('a');
      a.href = generatedAudio;
      a.download = `generated-${Date.now()}.wav`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const getVoiceBannerText = (voice) => {
    if (!voice) return '';
    const provider = getVoiceProviderLabel(voice);
    const voiceType = provider === 'Cloned' ? 'Cloned (Local)' : provider;
    const language = (voice.language_code || 'en').toUpperCase();
    const silenceRemoval = voice.remove_silence !== false ? 'Enabled' : 'Disabled';

    return `Type: ${voiceType} • Language: ${language} • Silence Removal: ${silenceRemoval}`;
  };

  const renderVoiceStatusBanner = (voice) => {
    if (!voice) return null;

    return (
      <Box
        sx={{
          mt: 1.5,
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: 'rgba(34,211,238,0.05)',
          border: '1px solid rgba(34,211,238,0.2)',
          borderRadius: 2,
        }}
      >
        <Tune sx={{ fontSize: 16, color: ACCENT }} />
        <Typography variant="body2" sx={{ color: '#bfc7d5' }}>
          {getVoiceBannerText(voice)}
        </Typography>
      </Box>
    );
  };

  return (
    <Box>
      <PageHeader
        overline="AI Studio"
        title="AI Content"
        subtitle="Generate scripts, speech and promos with your stored AI voices."
        icon={<AutoAwesome />}
        actions={
          <Chip
            icon={<RecordVoiceOver sx={{ fontSize: 16 }} />}
            label={`${voiceLimit}/${maxVoices} voices`}
            sx={{
              color: '#22d3ee',
              bgcolor: 'rgba(34,211,238,0.1)',
              border: '1px solid rgba(34,211,238,0.3)',
              fontWeight: 700,
              '& .MuiChip-icon': { color: '#22d3ee' },
            }}
          />
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2.5 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2.5 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Tabs
        value={tabValue}
        onChange={(e, newValue) => setTabValue(newValue)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3, borderBottom: '1px solid rgba(148,163,184,0.12)' }}
      >
        <Tab label="Generate Content" icon={<AutoAwesome sx={{ fontSize: 18 }} />} iconPosition="start" />
        <Tab label="Generate Speech" icon={<GraphicEq sx={{ fontSize: 18 }} />} iconPosition="start" />
        <Tab label="Prompts" icon={<EditNote sx={{ fontSize: 18 }} />} iconPosition="start" />
        <Tab label="Promo Manager" icon={<Campaign sx={{ fontSize: 18 }} />} iconPosition="start" />
        <Tab label="Content Sources" icon={<RssIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
        <Tab label={`My Voices (${voiceLimit}/${maxVoices})`} icon={<RecordVoiceOver sx={{ fontSize: 18 }} />} iconPosition="start" />
        <Tab label="Banned Words" icon={<Block sx={{ fontSize: 18 }} />} iconPosition="start" />
        <Tab label="Generated Links" icon={<PlaylistAddCheck sx={{ fontSize: 18 }} />} iconPosition="start" />
      </Tabs>

      {/* My Voices Tab */}
      {tabValue === 5 && (
        <Box>
          <Tabs
            value={myVoicesSubTab}
            onChange={(e, newValue) => setMyVoicesSubTab(newValue)}
            TabIndicatorProps={{ style: { display: 'none' } }}
            sx={{
              mb: 3,
              minHeight: 0,
              '& .MuiTabs-flexContainer': { gap: 1 },
              '& .MuiTab-root': {
                minHeight: 36,
                px: 2,
                borderRadius: 999,
                border: '1px solid rgba(148,163,184,0.2)',
                color: 'var(--rs-muted)',
                '&:hover': { color: 'var(--rs-text)', bgcolor: 'rgba(148,163,184,0.08)' },
                '&.Mui-selected': {
                  color: ACCENT,
                  borderColor: 'rgba(34,211,238,0.45)',
                  bgcolor: 'rgba(34,211,238,0.1)',
                },
              },
            }}
          >
            {voiceSubTabs.map((tab) => (
              <Tab
                key={tab.key}
                label={tab.label}
                icon={tab.key === 'my_voices' ? <RecordVoiceOver sx={{ fontSize: 16 }} /> : <CloudQueue sx={{ fontSize: 16 }} />}
                iconPosition="start"
              />
            ))}
          </Tabs>

          {/* My Voices List */}
          {activeVoiceSubTabKey === 'my_voices' && (
            <Card sx={sectionCardSx}>
              <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
                <SectionHeader
                  icon={<RecordVoiceOver />}
                  title={`My Voices (${voiceLimit}/${maxVoices})`}
                  subtitle="Your stored voices, ready for speech and content generation."
                  action={
                    <Button
                      variant="outlined"
                      startIcon={<Refresh />}
                      onClick={fetchClonedVoices}
                      sx={outlineButtonSx}
                    >
                      Refresh
                    </Button>
                  }
                />

                {voiceLimit >= maxVoices && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Maximum voice limit reached ({maxVoices}). Delete a voice to add a new one.
                  </Alert>
                )}

                {loadingVoices ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress sx={{ color: '#22d3ee' }} />
                  </Box>
                ) : clonedVoices.length === 0 ? (
                  <Alert severity="info">
                    No voices stored yet. Use the Add ElevenLabs, Speechify, or Google Voice tabs to create your first voice.
                  </Alert>
                ) : (
                  <>
                    <Alert severity="success" sx={{ mb: 2 }}>
                      {clonedVoices.length} voice{clonedVoices.length !== 1 ? 's' : ''} stored and ready to use
                    </Alert>
                    <List sx={{ py: 0 }}>
                      {clonedVoices.map((voice) => (
                        <React.Fragment key={voice.id}>
                          <ListItem
                            sx={{
                              bgcolor: selectedVoice?.id === voice.id ? 'rgba(34,211,238,0.1)' : 'var(--rs-surface)',
                              borderRadius: 2.5,
                              mb: 1.25,
                              py: 1.5,
                              border: selectedVoice?.id === voice.id
                                ? '1px solid rgba(34,211,238,0.6)'
                                : '1px solid rgba(148,163,184,0.14)',
                              transition: 'border-color 160ms ease, background-color 160ms ease, transform 160ms ease',
                              '&:hover': {
                                bgcolor: 'rgba(34,211,238,0.06)',
                                borderColor: 'rgba(34,211,238,0.35)',
                                transform: 'translateY(-1px)',
                              },
                            }}
                          >
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {voice.name}
                                  </Typography>
                                  <Chip
                                    label={getVoiceProviderLabel(voice)}
                                    size="small"
                                    sx={{
                                      bgcolor: getVoiceProviderColor(voice),
                                      color: 'var(--rs-text)',
                                      fontSize: '0.7rem',
                                      height: 20,
                                    }}
                                  />
                                  {voice.language_code && voice.language_code !== 'en' && (
                                    <Chip
                                      label={voice.language_code.toUpperCase()}
                                      size="small"
                                      sx={{ bgcolor: 'var(--rs-border)', color: 'var(--rs-text)', fontSize: '0.7rem', height: 20 }}
                                    />
                                  )}
                                </Box>
                              }
                              secondary={
                                <>
                                  {voice.description && (
                                    <>
                                      {voice.description}
                                      {' • '}
                                    </>
                                  )}
                                  Stored: {voice.created_at ? new Date(voice.created_at).toLocaleString() : 'Unknown'}
                                  {voice.reference_audio_url && (
                                    <>
                                      {' • '}
                                      <Button
                                        size="small"
                                        onClick={() => {
                                          const audio = new Audio(`${API_URL}${voice.reference_audio_url}`);
                                          audio.play();
                                        }}
                                        sx={{ color: '#22d3ee', minWidth: 'auto', p: 0.5 }}
                                      >
                                        Play Reference
                                      </Button>
                                    </>
                                  )}
                                </>
                              }
                            />
                            <ListItemSecondaryAction>
                              <IconButton
                                edge="end"
                                onClick={() => {
                                  setEditingVoice(voice);
                                  setBannedPhrases(voice.banned_phrases || []);
                                  setPronunciationRules(voice.pronunciation_rules || {});
                                  setVoiceSettings(voice.voice_settings || {});
                                  setEditingModelId(voice.elevenlabs_model_id || voice.speechify_model || '');
                                  setRemoveSilence(voice.remove_silence !== false);
                                  setSettingsDialogOpen(true);
                                }}
                                sx={{ color: '#ffa726', mr: 1, '&:hover': { bgcolor: 'rgba(255,167,38,0.12)' } }}
                                title="Voice settings (banned phrases, pronunciation, etc.)"
                              >
                                <Tune />
                              </IconButton>
                              <IconButton
                                edge="end"
                                onClick={() => {
                                  setSelectedVoice(voice);
                                  setTabValue(1); // Navigate to Generate Speech tab
                                }}
                                sx={{ color: '#22d3ee', mr: 1 }}
                                title="Use this voice to generate speech"
                              >
                                <PlayArrow />
                              </IconButton>
                              <IconButton
                                edge="end"
                                onClick={() => handleDeleteVoice(voice.id)}
                                sx={{ color: '#ff6b6b' }}
                                title="Delete this stored voice"
                              >
                                <Delete />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        </React.Fragment>
                      ))}
                    </List>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Add ElevenLabs Voice Sub-Tab */}
          {activeVoiceSubTabKey === 'elevenlabs' && (
            <Card sx={sectionCardSx}>
              <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
                <SectionHeader
                  icon={<CloudQueue />}
                  title="Add ElevenLabs Voice"
                  subtitle={`Add an ElevenLabs voice to your collection. You can use any voice from your ElevenLabs account. Maximum of ${maxVoices} voices total.`}
                />

                {voiceLimit >= maxVoices && (
                  <Alert severity="warning" sx={{ mb: 3 }}>
                    You have reached the maximum limit of {maxVoices} voices. Please delete a voice before adding a new one.
                  </Alert>
                )}

                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="body1" sx={{ color: 'var(--rs-text)' }}>
                        Available ElevenLabs Voices
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={async () => {
                          setLoadingElevenlabsVoices(true);
                          try {
                            const res = await axios.get(`${API_URL}/api/ai/voices/elevenlabs/list`, {
                              withCredentials: true,
                            });
                            setElevenlabsVoices(res.data?.voices || []);
                          } catch (err) {
                            console.error('Error fetching ElevenLabs voices:', err);
                            setError(err.response?.data?.message || 'Failed to load ElevenLabs voices. Make sure API key is configured.');
                          } finally {
                            setLoadingElevenlabsVoices(false);
                          }
                        }}
                        sx={outlineButtonSx}
                      >
                        {loadingElevenlabsVoices ? 'Loading...' : 'Load Voices'}
                      </Button>
                    </Box>
                    {elevenlabsVoices.length > 0 && (
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel sx={{ color: 'var(--rs-muted)' }}>Select ElevenLabs Voice</InputLabel>
                        <Select
                          value={elevenlabsVoiceId}
                          onChange={(e) => {
                            const voice = elevenlabsVoices.find((v) => v.voice_id === e.target.value);
                            setElevenlabsVoiceId(e.target.value);
                            if (voice && !elevenlabsName) {
                              setElevenlabsName(voice.name || '');
                            }
                          }}
                          sx={{ color: 'var(--rs-text)' }}
                        >
                          {elevenlabsVoices.map((voice) => (
                            <MenuItem key={voice.voice_id} value={voice.voice_id}>
                              {voice.name} {voice.labels && `(${voice.labels.gender || ''} ${voice.labels.age || ''})`.trim()}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Voice Name"
                      value={elevenlabsName}
                      onChange={(e) => setElevenlabsName(e.target.value)}
                      placeholder="e.g., Professional Male Voice"
                      sx={{ mb: 2 }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel sx={{ color: 'var(--rs-muted)' }}>Model</InputLabel>
                      <Select
                        value={elevenlabsModelId}
                        onChange={(e) => setElevenlabsModelId(e.target.value)}
                        sx={{ color: 'var(--rs-text)' }}
                      >
                        {ELEVENLABS_MODELS.map((model) => (
                          <MenuItem key={model.id} value={model.id}>{model.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Language Code"
                      value={elevenlabsLanguage}
                      onChange={(e) => setElevenlabsLanguage(e.target.value)}
                      placeholder="en, es, fr, de, etc."
                      helperText="ISO 639-1 language code (e.g., en, es, fr, de)"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      label="Description (Optional)"
                      value={elevenlabsDescription}
                      onChange={(e) => setElevenlabsDescription(e.target.value)}
                      placeholder="Optional description for this voice"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      onClick={async () => {
                        if (!elevenlabsName.trim()) {
                          setError('Please enter a voice name');
                          return;
                        }
                        if (!elevenlabsVoiceId) {
                          setError('Please select an ElevenLabs voice');
                          return;
                        }

                        setLoading(true);
                        setError('');
                        setSuccess('');

                        try {
                          const res = await axios.post(
                            `${API_URL}/api/ai/voices/elevenlabs`,
                            {
                              name: elevenlabsName.trim(),
                              elevenlabs_voice_id: elevenlabsVoiceId,
                              elevenlabs_model_id: elevenlabsModelId,
                              language_code: elevenlabsLanguage || 'en',
                              description: elevenlabsDescription || null,
                            },
                            { withCredentials: true }
                          );

                          setSuccess(`ElevenLabs voice "${elevenlabsName}" added and stored successfully!`);
                          setElevenlabsName('');
                          setElevenlabsVoiceId('');
                          setElevenlabsDescription('');
                          await fetchClonedVoices();
                          setTabValue(5); // Stay on "My Voices" tab
                          setMyVoicesSubTab(0); // Show the list
                        } catch (err) {
                          console.error('Error adding ElevenLabs voice:', err);
                          setError(err.response?.data?.message || 'Failed to add ElevenLabs voice');
                        } finally {
                          setLoading(false);
                        }
                      }}
                      disabled={loading || !elevenlabsName || !elevenlabsVoiceId || voiceLimit >= maxVoices}
                      startIcon={<Add />}
                      sx={ctaButtonSx}
                    >
                      {loading ? 'Adding...' : 'Add & Store Voice'}
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Add Speechify Voice Sub-Tab */}
          {activeVoiceSubTabKey === 'speechify' && (
            <Card sx={sectionCardSx}>
              <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
                <SectionHeader
                  icon={<CloudQueue />}
                  title="Add Speechify Voice"
                  subtitle={`Add a Speechify voice to your collection. You can use system or custom voices from your Speechify account. Maximum of ${maxVoices} voices total.`}
                />

                {voiceLimit >= maxVoices && (
                  <Alert severity="warning" sx={{ mb: 3 }}>
                    You have reached the maximum limit of {maxVoices} voices. Please delete a voice before adding a new one.
                  </Alert>
                )}

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel sx={{ color: 'var(--rs-muted)' }}>Model</InputLabel>
                      <Select
                        value={speechifyModel}
                        onChange={(e) => {
                          setSpeechifyModel(e.target.value);
                          setSpeechifyVoiceId('');
                          setSpeechifyVoices([]);
                        }}
                        sx={{ color: 'var(--rs-text)' }}
                      >
                        {SPEECHIFY_MODELS.map((model) => (
                          <MenuItem key={model.id} value={model.id}>{model.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body1" sx={{ color: 'var(--rs-text)' }}>
                        Available Speechify Voices
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={async () => {
                          setLoadingSpeechifyVoices(true);
                          try {
                            const res = await axios.get(`${API_URL}/api/ai/voices/speechify/list`, {
                              params: { model: speechifyModel },
                              withCredentials: true,
                            });
                            setSpeechifyVoices(res.data?.voices || []);
                          } catch (err) {
                            console.error('Error fetching Speechify voices:', err);
                            setError(err.response?.data?.message || 'Failed to load Speechify voices. Make sure API key is configured.');
                          } finally {
                            setLoadingSpeechifyVoices(false);
                          }
                        }}
                        sx={outlineButtonSx}
                      >
                        {loadingSpeechifyVoices ? 'Loading...' : 'Load Voices'}
                      </Button>
                    </Box>
                  </Grid>

                  {speechifyVoices.length > 0 && (
                    <>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Search voices..."
                          value={speechifySearch}
                          onChange={(e) => setSpeechifySearch(e.target.value)}
                          placeholder="Type to filter by name, locale, or tags..."
                          sx={{ mb: 1 }}
                        />
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                          {['all', 'male', 'female'].map((g) => (
                            <Chip
                              key={g}
                              label={g === 'all' ? 'All Genders' : g.charAt(0).toUpperCase() + g.slice(1)}
                              size="small"
                              onClick={() => setSpeechifyGenderFilter(g)}
                              sx={{
                                bgcolor: speechifyGenderFilter === g ? '#22d3ee' : 'var(--rs-border)',
                                color: 'var(--rs-text)',
                                cursor: 'pointer',
                                '&:hover': { bgcolor: speechifyGenderFilter === g ? '#0891b2' : '#3a4456' },
                              }}
                            />
                          ))}
                          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, bgcolor: '#5b6675' }} />
                          {['all', ...Array.from(new Set(speechifyVoices.map((v) => v.locale).filter(Boolean))).sort()].map((loc) => {
                            const localeLabels = {
                              all: 'All Regions',
                              'en-US': 'American',
                              'en-GB': 'British',
                              'en-AU': 'Australian',
                              'en-IN': 'Indian',
                              'en-IE': 'Irish',
                              'en-ZA': 'South African',
                              'en-NZ': 'New Zealand',
                              'en-SG': 'Singaporean',
                              'fr-FR': 'French',
                              'de-DE': 'German',
                              'es-ES': 'Spanish',
                              'it-IT': 'Italian',
                              'pt-BR': 'Brazilian Portuguese',
                              'ja-JP': 'Japanese',
                              'ko-KR': 'Korean',
                              'zh-CN': 'Chinese',
                              'hi-IN': 'Hindi',
                              'ar-SA': 'Arabic',
                            };
                            return (
                              <Chip
                                key={loc}
                                label={localeLabels[loc] || loc}
                                size="small"
                                onClick={() => setSpeechifyLocaleFilter(loc)}
                                sx={{
                                  bgcolor: speechifyLocaleFilter === loc ? '#f59e0b' : 'var(--rs-border)',
                                  color: 'var(--rs-text)',
                                  cursor: 'pointer',
                                  '&:hover': { bgcolor: speechifyLocaleFilter === loc ? '#f57c00' : '#3a4456' },
                                }}
                              />
                            );
                          })}
                        </Box>
                      </Grid>

                      <Grid item xs={12}>
                        <Paper sx={{ maxHeight: 320, overflow: 'auto', bgcolor: 'var(--rs-overlay-soft)', border: '1px solid rgba(148,163,184,0.14)', borderRadius: 2.5 }}>
                          <List dense>
                            {(() => {
                              const localeLabels = {
                                'en-US': 'American', 'en-GB': 'British', 'en-AU': 'Australian',
                                'en-IN': 'Indian', 'en-IE': 'Irish', 'en-ZA': 'South African',
                                'en-NZ': 'New Zealand', 'en-SG': 'Singaporean',
                                'fr-FR': 'French', 'de-DE': 'German', 'es-ES': 'Spanish',
                                'it-IT': 'Italian', 'pt-BR': 'Brazilian Portuguese',
                                'ja-JP': 'Japanese', 'ko-KR': 'Korean', 'zh-CN': 'Chinese',
                                'hi-IN': 'Hindi', 'ar-SA': 'Arabic',
                              };
                              const searchLower = speechifySearch.toLowerCase();
                              const filtered = speechifyVoices.filter((v) => {
                                if (speechifyGenderFilter !== 'all' && v.gender !== speechifyGenderFilter) return false;
                                if (speechifyLocaleFilter !== 'all' && v.locale !== speechifyLocaleFilter) return false;
                                if (searchLower) {
                                  const haystack = [
                                    v.name, v.gender, v.locale,
                                    localeLabels[v.locale] || '',
                                    ...(v.tags || []),
                                  ].join(' ').toLowerCase();
                                  return haystack.includes(searchLower);
                                }
                                return true;
                              });

                              const grouped = {};
                              filtered.forEach((v) => {
                                const key = v.locale || 'Unknown';
                                if (!grouped[key]) grouped[key] = [];
                                grouped[key].push(v);
                              });

                              const sortedKeys = Object.keys(grouped).sort((a, b) => {
                                if (a === 'en-US') return -1;
                                if (b === 'en-US') return 1;
                                if (a === 'en-GB') return -1;
                                if (b === 'en-GB') return 1;
                                return a.localeCompare(b);
                              });

                              if (filtered.length === 0) {
                                return (
                                  <ListItem>
                                    <ListItemText primary="No voices match your filters" sx={{ color: '#999' }} />
                                  </ListItem>
                                );
                              }

                              return sortedKeys.map((localeKey) => (
                                <React.Fragment key={localeKey}>
                                  <ListItem sx={{ bgcolor: 'rgba(22,27,37,0.9)', py: 0.5 }}>
                                    <ListItemText
                                      primary={
                                        <Typography variant="caption" sx={{ color: '#f59e0b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
                                          {localeLabels[localeKey] || localeKey} ({grouped[localeKey].length})
                                        </Typography>
                                      }
                                    />
                                  </ListItem>
                                  {grouped[localeKey].map((voice) => (
                                    <ListItem
                                      key={voice.voice_id}
                                      button
                                      selected={speechifyVoiceId === voice.voice_id}
                                      onClick={() => {
                                        setSpeechifyVoiceId(voice.voice_id);
                                        if (!speechifyName) {
                                          setSpeechifyName(voice.name || '');
                                        }
                                      }}
                                      sx={{
                                        pl: 3,
                                        bgcolor: speechifyVoiceId === voice.voice_id ? '#22d3ee20' : 'transparent',
                                        border: speechifyVoiceId === voice.voice_id ? '1px solid #22d3ee' : '1px solid transparent',
                                        borderRadius: 1,
                                        mx: 0.5,
                                        mb: 0.5,
                                        '&:hover': { bgcolor: '#22d3ee10' },
                                      }}
                                    >
                                      <ListItemText
                                        primary={
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                              {voice.name}
                                            </Typography>
                                            {voice.gender && (
                                              <Chip
                                                label={voice.gender}
                                                size="small"
                                                sx={{
                                                  height: 18, fontSize: '0.65rem',
                                                  bgcolor: voice.gender === 'male' ? '#1565c0' : voice.gender === 'female' ? '#ad1457' : '#5b6675',
                                                  color: 'var(--rs-text)',
                                                }}
                                              />
                                            )}
                                            {voice.type === 'personal' && (
                                              <Chip label="Custom" size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#22c55e', color: '#fff' }} />
                                            )}
                                            {voice.tags && voice.tags.length > 0 && voice.tags.map((tag) => (
                                              <Chip key={tag} label={tag} size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: 'var(--rs-border)', color: '#ccc' }} />
                                            ))}
                                          </Box>
                                        }
                                      />
                                    </ListItem>
                                  ))}
                                </React.Fragment>
                              ));
                            })()}
                          </List>
                        </Paper>
                        <Typography variant="caption" sx={{ color: '#666', mt: 0.5, display: 'block' }}>
                          {speechifyVoices.length} voices available
                          {(speechifyGenderFilter !== 'all' || speechifyLocaleFilter !== 'all' || speechifySearch) && ' (filtered)'}
                        </Typography>
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12}>
                    {speechifyVoiceId && (
                      <Alert severity="info" sx={{ mb: 1 }}>
                        Selected: <strong>{speechifyVoices.find(v => v.voice_id === speechifyVoiceId)?.name}</strong>
                        {' '}
                        {(() => {
                          const sv = speechifyVoices.find(v => v.voice_id === speechifyVoiceId);
                          if (!sv) return '';
                          const localeLabels = {
                            'en-US': 'American', 'en-GB': 'British', 'en-AU': 'Australian',
                            'en-IN': 'Indian', 'en-IE': 'Irish',
                          };
                          const parts = [];
                          if (sv.gender) parts.push(sv.gender);
                          if (sv.locale) parts.push(localeLabels[sv.locale] || sv.locale);
                          return parts.length ? `(${parts.join(', ')})` : '';
                        })()}
                      </Alert>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Voice Name"
                      value={speechifyName}
                      onChange={(e) => setSpeechifyName(e.target.value)}
                      placeholder="e.g., Energetic Host Voice"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Language Code"
                      value={speechifyLanguage}
                      onChange={(e) => setSpeechifyLanguage(e.target.value)}
                      placeholder="en-US, es-ES, fr-FR, de-DE, etc."
                      helperText="Language and region code (e.g., en-US, es-ES, fr-FR)"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      label="Description (Optional)"
                      value={speechifyDescription}
                      onChange={(e) => setSpeechifyDescription(e.target.value)}
                      placeholder="Optional description for this voice"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      onClick={async () => {
                        if (!speechifyName.trim()) {
                          setError('Please enter a voice name');
                          return;
                        }
                        if (!speechifyVoiceId) {
                          setError('Please select a Speechify voice');
                          return;
                        }

                        setLoading(true);
                        setError('');
                        setSuccess('');

                        try {
                          await axios.post(
                            `${API_URL}/api/ai/voices/speechify`,
                            {
                              name: speechifyName.trim(),
                              speechify_voice_id: speechifyVoiceId,
                              speechify_model: speechifyModel,
                              language_code: speechifyLanguage || 'en',
                              description: speechifyDescription || null,
                            },
                            { withCredentials: true }
                          );

                          setSuccess(`Speechify voice "${speechifyName}" added and stored successfully!`);
                          setSpeechifyName('');
                          setSpeechifyVoiceId('');
                          setSpeechifyDescription('');
                          await fetchClonedVoices();
                          setTabValue(5); // Stay on "My Voices" tab
                          setMyVoicesSubTab(0); // Show the list
                        } catch (err) {
                          console.error('Error adding Speechify voice:', err);
                          setError(err.response?.data?.message || 'Failed to add Speechify voice');
                        } finally {
                          setLoading(false);
                        }
                      }}
                      disabled={loading || !speechifyName || !speechifyVoiceId || voiceLimit >= maxVoices}
                      startIcon={<Add />}
                      sx={ctaButtonSx}
                    >
                      {loading ? 'Adding...' : 'Add & Store Voice'}
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          {activeVoiceSubTabKey === 'google' && (
            <Card sx={sectionCardSx}>
              <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
                <SectionHeader
                  icon={<CloudQueue />}
                  title="Add Google Voice"
                  subtitle={`Add a Google Cloud TTS voice. Chirp 3 HD voices are listed first. Maximum of ${maxVoices} voices total.`}
                />

                {voiceLimit >= maxVoices && (
                  <Alert severity="warning" sx={{ mb: 3 }}>
                    You have reached the maximum limit of {maxVoices} voices. Please delete a voice before adding a new one.
                  </Alert>
                )}

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Language Code"
                      value={googleLanguage}
                      onChange={(e) => {
                        setGoogleLanguage(e.target.value);
                        setGoogleVoiceId('');
                        setGoogleVoices([]);
                      }}
                      placeholder="en-US, en-GB, es-ES, fr-FR"
                      helperText="BCP-47 code. Chirp 3 HD voices use locale names like en-US-Chirp3-HD-Charon."
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '100%' }}>
                      <Typography variant="body1" sx={{ color: 'var(--rs-text)' }}>
                        Available Google Voices
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={async () => {
                          setLoadingGoogleVoices(true);
                          try {
                            const res = await axios.get(`${API_URL}/api/ai/voices/google/list`, {
                              params: { language_code: googleLanguage || DEFAULT_GOOGLE_LANGUAGE },
                              withCredentials: true,
                            });
                            setGoogleVoices(res.data?.voices || []);
                          } catch (err) {
                            console.error('Error fetching Google voices:', err);
                            setError(err.response?.data?.message || 'Failed to load Google voices. Make sure the API key is configured.');
                          } finally {
                            setLoadingGoogleVoices(false);
                          }
                        }}
                        sx={outlineButtonSx}
                      >
                        {loadingGoogleVoices ? 'Loading...' : 'Load Voices'}
                      </Button>
                    </Box>
                  </Grid>

                  {googleVoices.length > 0 && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Search voices..."
                        value={googleSearch}
                        onChange={(e) => setGoogleSearch(e.target.value)}
                        placeholder="Filter by name, gender, or Chirp 3 HD"
                        sx={{ mb: 1 }}
                      />
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1, mb: 2 }}>
                        {['all', 'MALE', 'FEMALE'].map((g) => (
                          <Chip
                            key={g}
                            label={g === 'all' ? 'All Genders' : g.charAt(0) + g.slice(1).toLowerCase()}
                            size="small"
                            onClick={() => setGoogleGenderFilter(g)}
                            sx={{
                              bgcolor: googleGenderFilter === g ? '#22d3ee' : 'var(--rs-border)',
                              color: 'var(--rs-text)',
                              cursor: 'pointer',
                            }}
                          />
                        ))}
                      </Box>
                      <Box sx={{ maxHeight: 280, overflow: 'auto', border: '1px solid rgba(148,163,184,0.14)', borderRadius: 2 }}>
                        <List dense>
                          {googleVoices
                            .filter((voice) => {
                              if (googleGenderFilter !== 'all' && voice.ssml_gender !== googleGenderFilter) return false;
                              if (!googleSearch.trim()) return true;
                              const q = googleSearch.toLowerCase();
                              return (
                                voice.name?.toLowerCase().includes(q) ||
                                voice.tier?.toLowerCase().includes(q) ||
                                (voice.language_codes || []).some((lang) => String(lang).toLowerCase().includes(q))
                              );
                            })
                            .map((voice) => (
                              <ListItem
                                key={voice.voice_id}
                                button
                                selected={googleVoiceId === voice.voice_id}
                                onClick={() => {
                                  setGoogleVoiceId(voice.voice_id);
                                  if (!googleName) {
                                    const shortName = String(voice.name || '').split('-').slice(-1)[0] || voice.name;
                                    setGoogleName(shortName);
                                  }
                                }}
                                sx={{
                                  bgcolor: googleVoiceId === voice.voice_id ? 'rgba(34,211,238,0.12)' : 'transparent',
                                }}
                              >
                                <ListItemText
                                  primary={voice.name}
                                  secondary={`${voice.tier || 'Other'}${voice.ssml_gender ? ` • ${voice.ssml_gender}` : ''}`}
                                />
                              </ListItem>
                            ))}
                        </List>
                      </Box>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Voice Name"
                      value={googleName}
                      onChange={(e) => setGoogleName(e.target.value)}
                      placeholder="e.g., Station Host"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      label="Description (Optional)"
                      value={googleDescription}
                      onChange={(e) => setGoogleDescription(e.target.value)}
                      placeholder="Optional description for this voice"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      onClick={async () => {
                        if (!googleName.trim()) {
                          setError('Please enter a voice name');
                          return;
                        }
                        if (!googleVoiceId) {
                          setError('Please select a Google voice');
                          return;
                        }

                        setLoading(true);
                        setError('');
                        setSuccess('');

                        try {
                          await axios.post(
                            `${API_URL}/api/ai/voices/google`,
                            {
                              name: googleName.trim(),
                              google_voice_name: googleVoiceId,
                              language_code: googleLanguage || DEFAULT_GOOGLE_LANGUAGE,
                              description: googleDescription || null,
                            },
                            { withCredentials: true }
                          );

                          setSuccess(`Google voice "${googleName}" added and stored successfully!`);
                          setGoogleName('');
                          setGoogleVoiceId('');
                          setGoogleDescription('');
                          await fetchClonedVoices();
                          setMyVoicesSubTab(0);
                        } catch (err) {
                          console.error('Error adding Google voice:', err);
                          setError(err.response?.data?.message || 'Failed to add Google voice');
                        } finally {
                          setLoading(false);
                        }
                      }}
                      disabled={loading || !googleName || !googleVoiceId || voiceLimit >= maxVoices}
                      startIcon={<Add />}
                      sx={ctaButtonSx}
                    >
                      {loading ? 'Adding...' : 'Add & Store Voice'}
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {/* Generate Content Tab - Generate content using prompts */}
      {tabValue === 0 && (
        <Card sx={sectionCardSx}>
          <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
            <SectionHeader
              icon={<AutoAwesome />}
              title="Generate Content Using Prompts"
              subtitle="Pick a prompt template and voice — the system writes a script from your prompt and context, then generates the audio."
            />

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: 'var(--rs-muted)' }}>Select Prompt</InputLabel>
                  <Select
                    value={selectedPrompt?.id || ''}
                    onChange={(e) => {
                      const prompt = prompts.find((p) => p.id === parseInt(e.target.value));
                      setSelectedPrompt(prompt);
                      setGenerateDurationSeconds(parseInt(prompt?.max_length, 10) || 15);
                    }}
                    label="Select Prompt"
                    sx={{ color: 'var(--rs-text)' }}
                  >
                    {prompts.length === 0 ? (
                      <MenuItem disabled>No prompts available. Create prompts in the Prompts tab.</MenuItem>
                    ) : (
                      prompts.map((prompt) => (
                        <MenuItem key={prompt.id} value={prompt.id}>
                          {prompt.name}{prompt.max_length ? ` · ${prompt.max_length}s` : ''}{prompt.folder_path ? ` (${prompt.folder_path})` : ''}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: 'var(--rs-muted)' }}>Select Voice</InputLabel>
                  <Select
                    value={selectedVoiceForContent?.id || ''}
                    onChange={(e) => {
                      const voice = clonedVoices.find((v) => v.id === parseInt(e.target.value));
                      setSelectedVoiceForContent(voice);
                    }}
                    label="Select Voice"
                    sx={{ color: 'var(--rs-text)' }}
                  >
                    {clonedVoices.length === 0 ? (
                      <MenuItem disabled>No voices available. Add voices in My Voices tab.</MenuItem>
                    ) : (
                      clonedVoices.map((voice) => (
                        <MenuItem key={voice.id} value={voice.id}>
                          {voice.name} ({getVoiceProviderLabel(voice)})
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>

              {selectedVoiceForContent && (
                <Grid item xs={12}>
                  {renderVoiceStatusBanner(selectedVoiceForContent)}
                </Grid>
              )}

              {selectedPrompt && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Script length (seconds)"
                    value={generateDurationSeconds}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === '') {
                        setGenerateDurationSeconds('');
                        return;
                      }
                      const parsed = parseInt(raw, 10);
                      if (!Number.isFinite(parsed)) return;
                      setGenerateDurationSeconds(Math.min(300, parsed));
                    }}
                    onBlur={() => {
                      setGenerateDurationSeconds((current) => {
                        const parsed = parseInt(current, 10);
                        return Number.isFinite(parsed) ? Math.min(300, Math.max(5, parsed)) : 15;
                      });
                    }}
                    inputProps={{ min: 5, max: 300 }}
                    helperText={`Grok will write about ${Math.round((parseInt(generateDurationSeconds, 10) || selectedPrompt.max_length || 15) * 2.5)} spoken words. Defaults from the prompt (${selectedPrompt.max_length || 15}s).`}
                  />
                </Grid>
              )}

              {selectedPrompt && (
                <Grid item xs={12}>
                  <Paper sx={insetPaperSx}>
                    <Typography variant="overline" sx={{ color: ACCENT, display: 'block', mb: 0.5 }}>
                      Prompt Template Preview
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#c6cfdd', mb: 1 }}>
                      {selectedPrompt.prompt_template}
                    </Typography>
                    {selectedPrompt.tags && selectedPrompt.tags.length > 0 && (
                      <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {selectedPrompt.tags.map((tag, idx) => (
                          <Chip key={idx} label={tag} size="small" sx={{ fontSize: '0.7rem', bgcolor: 'rgba(148,163,184,0.14)', color: 'var(--rs-text)' }} />
                        ))}
                      </Box>
                    )}
                  </Paper>
                </Grid>
              )}

              {/* Music Bed Options */}
              <Grid item xs={12}>
                <MusicBedControls
                  enabled={musicBedEnabled}
                  onToggle={setMusicBedEnabled}
                  categoryId={musicBedCategoryId}
                  onCategoryChange={setMusicBedCategoryId}
                  volumeDb={musicBedVolumeDb}
                  onVolumeChange={setMusicBedVolumeDb}
                  categories={musicBedCategories}
                  description="Play a music bed underneath the generated voicetrack at a lower volume."
                />
              </Grid>

              <Grid item xs={12}>
                {contentGenerationProgress && (
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CircularProgress size={20} sx={{ color: ACCENT }} />
                    <Typography variant="body2" sx={{ color: ACCENT }}>
                      {contentGenerationProgress}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    startIcon={<AutoAwesome />}
                    onClick={handleGenerateContent}
                    disabled={loading || !selectedPrompt || !selectedVoiceForContent}
                    sx={ctaButtonSx}
                  >
                    {loading ? 'Generating...' : 'Generate Content & Audio'}
                  </Button>
                  {generatedContentAudio && (
                    <>
                      <Button
                        variant="outlined"
                        startIcon={<PlayArrow />}
                        onClick={() => {
                          const audio = new Audio(generatedContentAudio);
                          audio.play();
                        }}
                        sx={outlineButtonSx}
                      >
                        Play
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<Download />}
                        onClick={() => {
                          const a = document.createElement('a');
                          a.href = generatedContentAudio;
                          a.download = `generated-content-${Date.now()}.wav`;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                        }}
                        sx={outlineButtonSx}
                      >
                        Download
                      </Button>
                    </>
                  )}
                </Box>
              </Grid>

              {generatedContentScript && (
                <Grid item xs={12}>
                  <Paper sx={insetPaperSx}>
                    <Typography variant="overline" sx={{ color: ACCENT, display: 'block', mb: 0.5 }}>
                      Generated Script
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'var(--rs-text)', whiteSpace: 'pre-wrap' }}>
                      {generatedContentScript}
                    </Typography>
                    {generatedContentLength?.seconds != null && (
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', mt: 0.5, color: generatedContentLength.overrun ? '#ffb74d' : 'var(--rs-text-muted)' }}
                      >
                        Reads in about {Math.round(generatedContentLength.seconds)}s
                        {generatedContentLength.overrun
                          ? ` — over the ${generatedContentLength.slot}s slot`
                          : ` of the ${generatedContentLength.slot}s slot`}
                      </Typography>
                    )}
                    {generatedContentWarnings.length > 0 && (
                      <Alert severity="warning" sx={{ mt: 1.5, borderRadius: 2 }}>
                        {generatedContentWarnings.map((warning, idx) => (
                          <Typography key={idx} variant="body2" component="div">
                            {warning}
                          </Typography>
                        ))}
                      </Alert>
                    )}
                  </Paper>
                </Grid>
              )}

              {generatedContentAudio && (
                <Grid item xs={12}>
                  <Paper sx={{ ...insetPaperSx, border: '1px solid rgba(34,211,238,0.3)' }}>
                    <Typography variant="overline" sx={{ color: ACCENT, display: 'block', mb: 0.5 }}>
                      Generated Audio
                    </Typography>
                    <audio controls src={generatedContentAudio} style={{ width: '100%' }} />
                  </Paper>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Generate Speech Tab */}
      {tabValue === 1 && (
        <Card sx={sectionCardSx}>
          <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
            <SectionHeader
              icon={<GraphicEq />}
              title="Generate Speech from Voice"
              subtitle="Select a voice (cloned, ElevenLabs, or Speechify) and generate speech from text. Banned phrases and pronunciation rules are applied automatically."
            />

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: 'var(--rs-muted)' }}>Select Voice</InputLabel>
                  <Select
                    value={selectedVoice?.id || ''}
                    onChange={(e) => {
                      const voice = clonedVoices.find((v) => v.id === parseInt(e.target.value));
                      setSelectedVoice(voice);
                      if (generatedAudioRef.current) { generatedAudioRef.current.pause(); }
                      setGeneratedAudio(null);
                      setIsPlayingGenerated(false);
                    }}
                    label="Select Voice"
                    sx={{ color: 'var(--rs-text)' }}
                  >
                    {clonedVoices.length === 0 ? (
                      <MenuItem disabled>No voices available. Add a voice first.</MenuItem>
                    ) : (
                      clonedVoices.map((voice) => (
                        <MenuItem key={voice.id} value={voice.id}>
                          {voice.name} ({getVoiceProviderLabel(voice)})
                          {voice.language_code && voice.language_code !== 'en' && ` - ${voice.language_code.toUpperCase()}`}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
                {renderVoiceStatusBanner(selectedVoice)}
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  label="Text to Generate"
                  value={ttsText}
                  onChange={(e) => setTtsText(e.target.value)}
                  placeholder="Enter the text you want to generate as speech..."
                  helperText="The cloned voice will speak this text"
                />
              </Grid>

              {/* Music Bed Options for Speech */}
              <Grid item xs={12}>
                <MusicBedControls
                  enabled={speechMusicBedEnabled}
                  onToggle={setSpeechMusicBedEnabled}
                  categoryId={speechMusicBedCategoryId}
                  onCategoryChange={setSpeechMusicBedCategoryId}
                  volumeDb={speechMusicBedVolumeDb}
                  onVolumeChange={setSpeechMusicBedVolumeDb}
                  categories={musicBedCategories}
                  description="Play a music bed underneath the generated speech at a lower volume."
                />
              </Grid>

              <Grid item xs={12}>
                {generationProgress && (
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CircularProgress size={20} sx={{ color: ACCENT }} />
                    <Typography variant="body2" sx={{ color: ACCENT }}>
                      {generationProgress}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    startIcon={<GraphicEq />}
                    onClick={handleGenerateTTS}
                    disabled={loading || !selectedVoice || !ttsText.trim()}
                    sx={ctaButtonSx}
                  >
                    {loading ? 'Generating...' : 'Generate Speech'}
                  </Button>
                  {generatedAudio && (
                    <>
                      <Button
                        variant="outlined"
                        startIcon={isPlayingGenerated ? <Pause /> : <PlayArrow />}
                        onClick={() => {
                          const el = generatedAudioRef.current;
                          if (!el) return;
                          if (isPlayingGenerated) { el.pause(); }
                          else { el.play().catch(() => {}); }
                        }}
                        sx={outlineButtonSx}
                      >
                        {isPlayingGenerated ? 'Pause' : 'Play'}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<Download />}
                        onClick={handleDownloadAudio}
                        sx={outlineButtonSx}
                      >
                        Download
                      </Button>
                    </>
                  )}
                </Box>
              </Grid>

              {generatedAudio && (
                <Grid item xs={12}>
                  <Paper sx={{ ...insetPaperSx, border: '1px solid rgba(34,211,238,0.3)' }}>
                    <Typography variant="overline" sx={{ color: ACCENT, display: 'block', mb: 0.5 }}>
                      Generated Audio
                    </Typography>
                    <audio
                      ref={generatedAudioRef}
                      controls
                      src={generatedAudio}
                      style={{ width: '100%' }}
                      onPlay={() => setIsPlayingGenerated(true)}
                      onPause={() => setIsPlayingGenerated(false)}
                      onEnded={() => setIsPlayingGenerated(false)}
                    />
                    <Typography variant="caption" sx={{ color: '#888', mt: 1, display: 'block' }}>
                      Saved to asset library under Voicelink
                    </Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Prompts Tab */}
      {tabValue === 2 && (
        <PromptGenerator stationId={null} voices={clonedVoices} />
      )}

      {/* Promo Manager Tab */}
      {tabValue === 3 && (
        <PromoManager stationId={null} voices={clonedVoices} prompts={prompts} />
      )}

      {/* Content Sources Tab: RSS feeds and news websites */}
      {tabValue === 4 && (
        <Box>
          <Card sx={sectionCardSx}>
            <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
              <SectionHeader
                icon={<RssIcon />}
                title="Content Sources"
                subtitle="RSS feeds and news websites, read on a cycle and used as live context when writing AI content."
                action={
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => {
                      setEditingRssFeed(null);
                      setRssPreview(null);
                      setRssForm(defaultRssForm());
                      setRssFeedDialogOpen(true);
                    }}
                    sx={ctaButtonSx}
                  >
                    Add Source
                  </Button>
                }
              />

              {loadingRssFeeds ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress sx={{ color: '#22d3ee' }} />
                </Box>
              ) : rssFeeds.length === 0 ? (
                <Alert severity="info">
                  No content sources configured. Add a news website such as bbc.co.uk/news, or a feed URL, to give the AI something current to talk about.
                </Alert>
              ) : (
                <List sx={{ py: 0 }}>
                  {rssFeeds.map((feed) => {
                    const isWebsite = feed.source_type === 'website';
                    const runLimit = feed.max_runs_per_day || 5;
                    const runsToday = feed.run_day
                      && new Date(feed.run_day).toDateString() === new Date().toDateString()
                      ? (feed.runs_today || 0) : 0;
                    const itemCount = Array.isArray(feed.cached_items) ? feed.cached_items.length : 0;
                    return (
                      <ListItem
                        key={feed.id}
                        sx={{
                          bgcolor: 'var(--rs-surface)',
                          borderRadius: 2.5,
                          mb: 1.25,
                          py: 1.5,
                          alignItems: 'flex-start',
                          border: '1px solid rgba(148,163,184,0.14)',
                          transition: 'border-color 160ms ease, background-color 160ms ease',
                          '&:hover': { borderColor: 'rgba(34,211,238,0.35)', bgcolor: 'rgba(34,211,238,0.04)' },
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {feed.name}
                              </Typography>
                              <Chip
                                label={isWebsite ? 'Website' : 'RSS'}
                                size="small"
                                sx={{
                                  fontSize: '0.65rem',
                                  height: 20,
                                  bgcolor: isWebsite ? 'rgba(59,130,246,0.16)' : 'rgba(34,211,238,0.16)',
                                  color: isWebsite ? '#93c5fd' : ACCENT,
                                }}
                              />
                              {feed.is_active === false && (
                                <Chip label="Inactive" size="small" sx={{ fontSize: '0.65rem', height: 20 }} />
                              )}
                              {feed.last_status === 'error' && (
                                <Chip label="Last read failed" size="small" color="error" sx={{ fontSize: '0.65rem', height: 20 }} />
                              )}
                            </Box>
                          }
                          secondary={
                            <>
                              <Typography variant="body2" sx={{ color: '#999', mb: 0.5, wordBreak: 'break-all' }}>
                                {feed.url}
                              </Typography>
                              {feed.resolved_url && feed.resolved_url !== feed.url && (
                                <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', wordBreak: 'break-all' }}>
                                  Reading {feed.resolved_url}
                                </Typography>
                              )}
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.75 }}>
                                <Chip
                                  label={`Cycle: ${runLimit}× a day`}
                                  size="small"
                                  sx={{ fontSize: '0.65rem', height: 20 }}
                                />
                                {feed.refresh_interval_minutes ? (
                                  <Chip
                                    label={`Every ${feed.refresh_interval_minutes} min`}
                                    size="small"
                                    sx={{ fontSize: '0.65rem', height: 20 }}
                                  />
                                ) : (
                                  <Chip
                                    label={`~Every ${Math.max(5, Math.ceil((24 * 60) / runLimit))} min`}
                                    size="small"
                                    sx={{ fontSize: '0.65rem', height: 20 }}
                                  />
                                )}
                                <Chip
                                  label={`${runsToday}/${runLimit} read today`}
                                  size="small"
                                  sx={{ fontSize: '0.65rem', height: 20 }}
                                />
                                <Chip
                                  label={`${itemCount} item${itemCount === 1 ? '' : 's'} ready`}
                                  size="small"
                                  sx={{ fontSize: '0.65rem', height: 20 }}
                                />
                                {feed.cached_at && (
                                  <Chip
                                    label={`Read ${new Date(feed.cached_at).toLocaleString()}`}
                                    size="small"
                                    sx={{ fontSize: '0.65rem', height: 20 }}
                                  />
                                )}
                              </Box>
                              {feed.last_status === 'error' && feed.last_error && (
                                <Typography variant="caption" sx={{ color: '#ff8a80', display: 'block', mt: 0.75 }}>
                                  {feed.last_error}
                                </Typography>
                              )}
                            </>
                          }
                        />
                        <ListItemSecondaryAction>
                          <Tooltip title="Read now">
                            <span>
                              <IconButton
                                edge="end"
                                onClick={() => handleRefreshRssSource(feed.id)}
                                disabled={refreshingSourceId === feed.id}
                                sx={{ color: ACCENT, mr: 1 }}
                              >
                                {refreshingSourceId === feed.id
                                  ? <CircularProgress size={18} sx={{ color: ACCENT }} />
                                  : <Refresh />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <IconButton
                            edge="end"
                            onClick={() => handleEditRssFeed(feed)}
                            sx={{ color: '#22d3ee', mr: 1 }}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            edge="end"
                            onClick={() => handleDeleteRssFeed(feed.id)}
                            sx={{ color: '#ff6b6b' }}
                          >
                            <Delete />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {tabValue === 6 && (
        <BannedWordsManager stationId={null} />
      )}

      {/* Generated Links Tab: review / approve links the auto-generator has produced */}
      {tabValue === 7 && (
        <GeneratedLinksReview />
      )}

      {/* Voice Settings Dialog */}
      <Dialog
        open={settingsDialogOpen}
        onClose={() => {
          setSettingsDialogOpen(false);
          setEditingVoice(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--rs-paper)', color: 'var(--rs-text)' } }}
      >
        <DialogTitle sx={{ color: '#22d3ee' }}>
          Voice Settings: {editingVoice?.name}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ color: '#22d3ee', mb: 1 }}>
                Banned Phrases
              </Typography>
              <Typography variant="caption" sx={{ color: '#999', mb: 2, display: 'block' }}>
                Phrases that should not be spoken by this voice (one per line)
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={bannedPhrases.join('\n')}
                onChange={(e) => {
                  const phrases = e.target.value.split('\n').filter((p) => p.trim());
                  setBannedPhrases(phrases);
                }}
                placeholder="Enter banned phrases, one per line..."
                sx={{ mb: 2 }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ color: '#22d3ee', mb: 1 }}>
                Pronunciation Rules
              </Typography>
              <Typography variant="caption" sx={{ color: '#999', mb: 2, display: 'block' }}>
                Map words/phrases to their pronunciations (format: word=pronunciation, one per line)
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={Object.entries(pronunciationRules)
                  .map(([key, value]) => `${key}=${value}`)
                  .join('\n')}
                onChange={(e) => {
                  const rules = {};
                  e.target.value.split('\n').forEach((line) => {
                    const [key, value] = line.split('=').map((s) => s.trim());
                    if (key && value) {
                      rules[key] = value;
                    }
                  });
                  setPronunciationRules(rules);
                }}
                placeholder="word=pronunciation&#10;example=eg-zam-pul"
                helperText="Format: word=pronunciation (one per line)"
                sx={{ mb: 2 }}
              />
            </Grid>

            {(isElevenLabsVoice(editingVoice) || isSpeechifyVoice(editingVoice) || isGoogleVoice(editingVoice)) && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ color: '#22d3ee', mb: 1 }}>
                  Voice Settings (Cloud Voice)
                </Typography>
                <Grid container spacing={2}>
                  {isElevenLabsVoice(editingVoice) && (
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel sx={{ color: 'var(--rs-muted)' }}>ElevenLabs Model</InputLabel>
                        <Select
                          value={editingModelId || 'eleven_v4'}
                          onChange={(e) => setEditingModelId(e.target.value)}
                          sx={{ color: 'var(--rs-text)' }}
                        >
                          {ELEVENLABS_MODELS.map((model) => (
                            <MenuItem key={model.id} value={model.id}>{model.label}</MenuItem>
                          ))}
                          {editingModelId && !ELEVENLABS_MODELS.some((model) => model.id === editingModelId) && (
                            <MenuItem value={editingModelId}>{editingModelId} (stored)</MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  {isSpeechifyVoice(editingVoice) && (
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel sx={{ color: 'var(--rs-muted)' }}>Speechify Model</InputLabel>
                        <Select
                          value={editingModelId || 'simba-3.2'}
                          onChange={(e) => setEditingModelId(e.target.value)}
                          sx={{ color: 'var(--rs-text)' }}
                        >
                          {SPEECHIFY_MODELS.map((model) => (
                            <MenuItem key={model.id} value={model.id}>{model.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  {isElevenLabsVoice(editingVoice) && (
                    <>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Stability"
                      value={voiceSettings.stability ?? 0.5}
                      onChange={(e) =>
                        setVoiceSettings({ ...voiceSettings, stability: parseFloat(e.target.value) })
                      }
                      inputProps={{ min: 0, max: 1, step: 0.1 }}
                      helperText="0.0 - 1.0"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Similarity Boost"
                      value={voiceSettings.similarity_boost ?? 0.75}
                      onChange={(e) =>
                        setVoiceSettings({ ...voiceSettings, similarity_boost: parseFloat(e.target.value) })
                      }
                      inputProps={{ min: 0, max: 1, step: 0.1 }}
                      helperText="0.0 - 1.0"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Style"
                      value={voiceSettings.style ?? 0.0}
                      onChange={(e) =>
                        setVoiceSettings({ ...voiceSettings, style: parseFloat(e.target.value) })
                      }
                      inputProps={{ min: 0, max: 1, step: 0.1 }}
                      helperText="0.0 - 1.0"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <InputLabel sx={{ color: 'var(--rs-muted)' }}>Speaker Boost</InputLabel>
                      <Select
                        value={voiceSettings.use_speaker_boost !== false ? 'true' : 'false'}
                        onChange={(e) =>
                          setVoiceSettings({ ...voiceSettings, use_speaker_boost: e.target.value === 'true' })
                        }
                        sx={{ color: 'var(--rs-text)' }}
                      >
                        <MenuItem value="true">Enabled</MenuItem>
                        <MenuItem value="false">Disabled</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                    </>
                  )}
                  {isGoogleVoice(editingVoice) && (
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Speaking Rate"
                        value={voiceSettings.speaking_rate ?? 1.0}
                        onChange={(e) =>
                          setVoiceSettings({ ...voiceSettings, speaking_rate: parseFloat(e.target.value) })
                        }
                        inputProps={{ min: 0.25, max: 2, step: 0.05 }}
                        helperText="0.25 - 2.0 (Chirp 3 HD pace)"
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            )}

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: 'var(--rs-muted)' }}>Silence Removal</InputLabel>
                <Select
                  value={removeSilence ? 'true' : 'false'}
                  onChange={(e) => setRemoveSilence(e.target.value === 'true')}
                  sx={{ color: 'var(--rs-text)' }}
                >
                  <MenuItem value="true">Enabled (removes awkward pauses)</MenuItem>
                  <MenuItem value="false">Disabled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(148,163,184,0.14)' }}>
          <Button
            onClick={() => {
              setSettingsDialogOpen(false);
              setEditingVoice(null);
            }}
            sx={{ color: 'var(--rs-muted)', borderRadius: 2.5 }}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              try {
                await axios.put(
                  `${API_URL}/api/ai/voices/${editingVoice.id}`,
                  {
                    banned_phrases: bannedPhrases,
                    pronunciation_rules: pronunciationRules,
                    voice_settings: voiceSettings,
                    remove_silence: removeSilence,
                    ...(isElevenLabsVoice(editingVoice) ? { elevenlabs_model_id: editingModelId } : {}),
                    ...(isSpeechifyVoice(editingVoice) ? { speechify_model: editingModelId } : {}),
                  },
                  { withCredentials: true }
                );
                setSuccess('Voice settings updated successfully!');
                setSettingsDialogOpen(false);
                setEditingVoice(null);
                await fetchClonedVoices();
              } catch (err) {
                console.error('Error updating voice settings:', err);
                setError(err.response?.data?.message || 'Failed to update voice settings');
              }
            }}
            variant="contained"
            sx={ctaButtonSx}
          >
            Save Settings
          </Button>
        </DialogActions>
      </Dialog>

      {/* Content Source Dialog: RSS feed or news website, plus its cycle */}
      <Dialog
        open={rssFeedDialogOpen}
        onClose={() => {
          setRssFeedDialogOpen(false);
          setEditingRssFeed(null);
          setRssPreview(null);
          setRssForm(defaultRssForm());
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--rs-paper)', color: 'var(--rs-text)' } }}
      >
        <DialogTitle sx={{ color: '#22d3ee' }}>
          {editingRssFeed ? 'Edit Content Source' : 'Add Content Source'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: 'var(--rs-muted)' }}>Source Type</InputLabel>
                <Select
                  value={rssForm.sourceType}
                  onChange={(e) => setRssForm({ ...rssForm, sourceType: e.target.value })}
                  sx={{ color: 'var(--rs-text)' }}
                  label="Source Type"
                >
                  <MenuItem value="website">Website (news page)</MenuItem>
                  <MenuItem value="rss">RSS feed</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 0.5 }}>
                {rssForm.sourceType === 'website'
                  ? "Paste a news page such as bbc.co.uk/news. The site's own feed is used when it has one; otherwise its newest articles are read."
                  : 'Use a feed address such as feeds.bbci.co.uk/news/rss.xml.'}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Source Name"
                value={rssForm.name}
                onChange={(e) => setRssForm({ ...rssForm, name: e.target.value })}
                required
                placeholder="e.g., BBC News"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={rssForm.sourceType === 'website' ? 'Website URL' : 'RSS Feed URL'}
                value={rssForm.url}
                onChange={(e) => {
                  setRssForm({ ...rssForm, url: e.target.value });
                  setRssPreview(null);
                }}
                required
                placeholder={rssForm.sourceType === 'website' ? 'https://www.bbc.co.uk/news' : 'https://feeds.bbci.co.uk/news/rss.xml'}
                helperText={rssForm.sourceType === 'website'
                  ? 'A news index page, not a single article'
                  : 'The full URL of the feed'}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handlePreviewRssSource}
                  disabled={previewingSource || !rssForm.url.trim()}
                  sx={outlineButtonSx}
                >
                  {previewingSource ? 'Checking…' : 'Check source'}
                </Button>
                {rssPreview?.ok && (
                  <Typography variant="caption" sx={{ color: '#4ade80' }}>
                    {rssPreview.note || `${rssPreview.items?.length || 0} item(s) found`}
                  </Typography>
                )}
                {rssPreview?.ok === false && (
                  <Typography variant="caption" sx={{ color: '#ff8a80' }}>
                    {rssPreview.error}
                  </Typography>
                )}
              </Box>

              {rssPreview?.ok && (rssPreview.items?.length || 0) > 0 && (
                <Paper sx={{ ...insetPaperSx, mt: 1.5, maxHeight: 190, overflowY: 'auto' }}>
                  <Typography variant="overline" sx={{ color: ACCENT, display: 'block', mb: 0.5 }}>
                    Preview
                  </Typography>
                  {rssPreview.items.map((item, index) => (
                    <Box key={`${item.link}-${index}`} sx={{ mb: 1 }}>
                      <Typography variant="body2" sx={{ color: 'var(--rs-text)', fontWeight: 600 }}>
                        {index + 1}. {item.title}
                      </Typography>
                      {item.description && (
                        <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
                          {item.description.length > 180 ? `${item.description.slice(0, 180)}…` : item.description}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Paper>
              )}
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1, bgcolor: 'var(--rs-border)' }} />
              <Typography variant="subtitle2" sx={{ color: ACCENT, mb: 0.5 }}>
                Reading cycle
              </Typography>
              <Typography variant="caption" sx={{ color: '#999', display: 'block', mb: 1.5 }}>
                The source is read on this schedule and the results are reused for every link, so a website is not hammered once per link.
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Max reads per day"
                value={rssForm.maxRunsPerDay}
                onChange={(e) => setRssForm({ ...rssForm, maxRunsPerDay: parseInt(e.target.value, 10) || 5 })}
                inputProps={{ min: 1, max: 96 }}
                helperText="Hard ceiling on fetches per day"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Minutes between reads"
                value={rssForm.refreshIntervalMinutes}
                onChange={(e) => setRssForm({ ...rssForm, refreshIntervalMinutes: e.target.value })}
                inputProps={{ min: 5, max: 1440 }}
                placeholder={String(Math.max(5, Math.ceil((24 * 60) / (rssForm.maxRunsPerDay || 5))))}
                helperText="Blank spaces the daily allowance evenly"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Articles per read"
                value={rssForm.maxItems}
                onChange={(e) => setRssForm({ ...rssForm, maxItems: parseInt(e.target.value, 10) || 5 })}
                inputProps={{ min: 1, max: 50 }}
                helperText="Items kept from this source"
              />
            </Grid>
            {rssForm.sourceType === 'website' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Article links to open"
                  value={rssForm.maxLinks}
                  onChange={(e) => setRssForm({ ...rssForm, maxLinks: parseInt(e.target.value, 10) || 5 })}
                  inputProps={{ min: 1, max: 20 }}
                  helperText="Used only when the site has no feed"
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rssForm.isActive}
                    onChange={(e) => setRssForm({ ...rssForm, isActive: e.target.checked })}
                    sx={{ color: '#22d3ee', '&.Mui-checked': { color: '#22d3ee' } }}
                  />
                }
                label="Active"
                sx={{ color: 'var(--rs-text)' }}
              />
              <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 0.5 }}>
                Inactive sources are not read and are not used in content generation
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(148,163,184,0.14)' }}>
          <Button
            onClick={() => {
              setRssFeedDialogOpen(false);
              setEditingRssFeed(null);
              setRssPreview(null);
              setRssForm(defaultRssForm());
            }}
            sx={{ color: 'var(--rs-muted)', borderRadius: 2.5 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRssFeedSubmit}
            disabled={loading || !rssForm.name.trim() || !rssForm.url.trim()}
            variant="contained"
            sx={ctaButtonSx}
          >
            {loading ? 'Saving...' : editingRssFeed ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
